import { ConfirmModal, Loading, Table, TableRowMenuCell } from '@/components';
import { useMasterTablePermission } from '@/features/authentication/_hooks/permission';
import { useMessage } from '@/providers';
import {
  NEST_ITEM_CONNECTOR,
  TABLE_DATETIME_WIDTH,
  TABLE_ROW_MENU_WIDTH,
  datetimeFormat,
  findUpperRecursive,
} from '@/utils';
import dayjs from 'dayjs';
import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import invariant from 'tiny-invariant';
import {
  useCreateExpenditureItemNaturePreset,
  useDeleteExpenditureItemNaturePreset,
  useExpenditureItemNaturePresets,
  useExpenditureItemNatures,
  useExpenditureNatures,
  useUpdateExpenditureItemNaturePreset,
} from '../../hooks';
import {
  DEFAULT_PAGINATION_SETTING,
  expenditureNatureNameWithCode,
} from '../../utils';
import { useDrawerState } from '../DrawerStateProvider';
import { ExpenditureItemNaturePresetFormDrawer } from './ExpenditureItemNaturePresetFormDrawer';

export const ExpenditureItemNaturePresetsPage: FC = () => {
  const { t } = useTranslation();
  const { drawerState, setDrawerState } = useDrawerState();
  const { openMessage } = useMessage();
  const expenditureItemNaturePresets = useExpenditureItemNaturePresets();
  const expenditureNatures = useExpenditureNatures();
  const expenditureItemNatures = useExpenditureItemNatures();
  const { mutateAsync: createAsync } = useCreateExpenditureItemNaturePreset();
  const { mutateAsync: updateAsync } = useUpdateExpenditureItemNaturePreset();
  const { mutateAsync: deleteAsync } = useDeleteExpenditureItemNaturePreset();
  const editPermission = useMasterTablePermission(true);
  const [expenditureItemNatureDelete, setExpenditureItemNatureDelete] =
    useState<string | null>(null);

  const selected = expenditureItemNaturePresets.data?.find(
    (d) =>
      drawerState.open &&
      drawerState.type === 'expenditure-item-nature-preset' &&
      drawerState.isEditing &&
      drawerState.id === d.id,
  );

  if (
    expenditureItemNaturePresets.isPending ||
    !expenditureItemNaturePresets.data
  ) {
    return <Loading />;
  }

  return (
    <>
      <Table
        dataSource={expenditureItemNaturePresets.data}
        rowKey="id"
        columns={[
          {
            title: t('expenditure-nature'),
            key: 'expenditureNatureId',
            dataIndex: 'expenditureNatureId',
            render: (value) =>
              findUpperRecursive(value, expenditureNatures.data ?? [])
                .reverse()
                .flatMap((d) =>
                  d.nationalExpenditureNature
                    ? [
                        expenditureNatureNameWithCode({
                          code: d.nationalExpenditureNature.code,
                          name: d.nationalExpenditureNature.name,
                        }),
                        expenditureNatureNameWithCode({
                          code: d.code,
                          name: d.name,
                          level: d.level,
                        }),
                      ]
                    : [
                        expenditureNatureNameWithCode({
                          code: d.code,
                          name: d.name,
                          level: d.level,
                        }),
                      ],
                )
                .join(NEST_ITEM_CONNECTOR),
          },
          {
            title: t('expenditure-item-nature'),
            key: 'expenditureItemNatureId',
            dataIndex: 'expenditureItemNatureId',
            render: (value) =>
              findUpperRecursive(value, expenditureItemNatures.data ?? [], [])
                .map((d) =>
                  expenditureNatureNameWithCode({
                    code: d.code,
                    name: d.name,
                    level: d.level,
                  }),
                )
                .reverse()
                .join(NEST_ITEM_CONNECTOR),
          },
          {
            title: t('created-datetime'),
            key: 'createdAt',
            dataIndex: 'createdAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          {
            title: t('updated-datetime'),
            key: 'updatedAt',
            dataIndex: 'updatedAt',
            width: TABLE_DATETIME_WIDTH,
            render: (value) => datetimeFormat({ datetime: value }),
          },
          ...(editPermission.data
            ? [
                {
                  title: '',
                  key: 'actions',
                  fixed: 'right' as const,
                  width: TABLE_ROW_MENU_WIDTH,
                  onCell: () => ({
                    style: { minWidth: TABLE_ROW_MENU_WIDTH },
                  }),
                  render: (_, record) => (
                    <TableRowMenuCell
                      onEdit={() => {
                        setDrawerState({
                          open: true,
                          type: 'expenditure-item-nature-preset',
                          isEditing: true,
                          id: record.id,
                        });
                      }}
                      items={[
                        {
                          key: 'delete',
                          label: t('delete'),
                          danger: true,
                          onClick: () =>
                            setExpenditureItemNatureDelete(record.id),
                        },
                      ]}
                    />
                  ),
                },
              ]
            : []),
        ]}
        pagination={DEFAULT_PAGINATION_SETTING}
      />
      <ExpenditureItemNaturePresetFormDrawer
        {...(drawerState.open && drawerState.isEditing
          ? {
              type: 'edit',
              initialValues: {
                expenditureNatureIds: selected
                  ? findUpperRecursive(
                      selected.expenditureNatureId,
                      expenditureNatures.data ?? [],
                      [],
                    )
                      .reverse()
                      .flatMap((d) =>
                        d.nationalExpenditureNatureId
                          ? [d.nationalExpenditureNatureId, d.id]
                          : [d.id],
                      )
                  : [],
                expenditureItemNatureIds: selected
                  ? findUpperRecursive(
                      selected.expenditureItemNatureId,
                      expenditureItemNatures.data ?? [],
                      [],
                    )
                      .map((d) => d.id)
                      .reverse()
                  : [],
                period: [
                  selected?.startYear
                    ? dayjs().year(selected.startYear)
                    : undefined,
                  selected?.endYear
                    ? dayjs().year(selected.endYear)
                    : undefined,
                ],
              },
            }
          : { type: 'new' })}
        onSubmit={async (values) => {
          invariant(drawerState.open, t('unreachable-code'));

          drawerState.isEditing
            ? await updateAsync({ ...values, id: drawerState.id })
            : await createAsync(values);

          await Promise.all([
            openMessage(
              'success',
              drawerState.isEditing
                ? t('message-update-completed')
                : t('message-create-completed'),
            ),
            expenditureItemNaturePresets.refetch(),
            (async () => {
              setDrawerState({ open: false });
            })(),
          ]);
        }}
      />
      <ConfirmModal
        open={expenditureItemNatureDelete !== null}
        title={t('delete-confirmation')}
        disclaimerText={t('delete-confirmation-disclaimer')}
        onOk={async () => {
          invariant(expenditureItemNatureDelete, t('unreachable-code'));
          deleteAsync(expenditureItemNatureDelete)
            .then(async () => {
              await Promise.all([
                openMessage('success', t('message-delete-completed')),
                (async () => {
                  setExpenditureItemNatureDelete(null);
                })(),
              ]);
            })
            .catch(async (e) => {
              await openMessage('error', t('message-delete-failed'));
              throw e;
            });
        }}
        onCancel={() => setExpenditureItemNatureDelete(null)}
        skipCheck
      />
    </>
  );
};
